'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Menu } from 'lucide-react'
import { ProfileDropdown } from './ProfileDropdown'
import { NavUpgradeButton } from './NavUpgradeButton'

// Fixed strip covering the floating header's own top gap (the 16px it sits
// down from the viewport edge at lg+). Painted the same flat grey as the
// page background and layered above scrolling content but below the header
// itself, so that gap always reads as empty page background — never a peek
// of whatever content happens to be scrolled up behind it.
export function HeaderTopGapMask() {
  return <div className="hidden lg:block fixed top-0 inset-x-0 h-4 bg-slate-100 z-40" aria-hidden="true" />
}

// Shared header for every authenticated app page — logo, mobile sidebar
// toggle, Contact link, upgrade button, profile menu. Every one of these
// pages used to paste its own copy of this exact markup (which is why a
// single styling change here used to mean a 25+ file sweep, twice); this
// is the single source of truth going forward. `showContact`/`showUpgrade`
// exist for the handful of pages that deliberately show a reduced header
// (no props needed for the common case — every default matches what almost
// every page already had).
export function AppHeader({
  onMenuClick,
  showMenuButton = true,
  showContact = true,
  showUpgrade = true,
  homeHref = '/dashboard',
}: {
  onMenuClick?: () => void
  showMenuButton?: boolean
  showContact?: boolean
  showUpgrade?: boolean
  // Preview-style pages reachable while logged out (e.g. cv/start/*) point
  // the logo at '/' instead of '/dashboard' for anonymous visitors.
  homeHref?: string
}) {
  // AppHeader has no per-page `active` prop the way MarketingHeader does
  // (threading one through 25+ pages just for this one link isn't worth
  // it) — pathname detection is self-contained and gives Contact the same
  // "current page" highlight Sidebar.tsx's own nav items already have.
  const isContactActive = usePathname() === '/contact'

  return (
    // Desktop (lg+) floats this as its own rounded/shadowed "island" panel,
    // 16px in from the top/sides, matching Sidebar.tsx's own floating
    // offsets (which are computed from this header's height + gap — see its
    // comment). Below lg it stays the original edge-to-edge flush bar, same
    // reasoning as Sidebar.tsx: floating chrome is a desktop-only treatment.
    <>
      <HeaderTopGapMask />
      <header className="bg-[linear-gradient(135deg,#0284c7_0%,#0369a1_45%,#0c4a6e_100%)] border-b border-primary-700 sticky top-0 z-50 shadow-md lg:top-4 lg:mx-4 lg:rounded-2xl lg:shadow-lg lg:border-b-0">
        <div className="px-4 py-3 sm:py-4 flex justify-between items-center">
          {/* lg:-ml-4 cancels this row's own px-4 so the group below starts
              flush with the header's own left edge (header-relative x:0) —
              the same reference point Sidebar.tsx's left-4 uses (both float
              lg:mx-4 in from the viewport). lg:w-56 then matches the
              sidebar's own width exactly (w-56), so centering the logo in
              this group puts it on the sidebar's true horizontal centre,
              not just visually close to it. Below lg there's no sidebar to
              match, so none of this applies — ordinary gap-3 layout. */}
          <div className="flex items-center gap-3 lg:-ml-4 lg:w-56 lg:justify-center">
            {showMenuButton && (
              <button
                onClick={onMenuClick}
                className="lg:hidden p-1.5 rounded-lg text-primary-100 hover:bg-primary-600 hover:text-white transition-colors"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
              </button>
            )}
            <Link href={homeHref} className="text-xl sm:text-3xl font-bold text-white">CVCircuit</Link>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Hidden below 390px: with the menu button, logo, upgrade button
                and profile button there's no room left (it butted against the
                logo at 375px and pushed the profile button off-screen at
                320px). Sidebar.tsx's mobile drawer carries the link instead. */}
            {showContact && (
              <Link href="/contact" className={`hidden min-[390px]:inline text-sm font-bold transition-colors ${isContactActive ? 'text-white' : 'text-primary-100 hover:text-white'}`}>Contact</Link>
            )}
            {showUpgrade && <NavUpgradeButton />}
            <ProfileDropdown />
          </div>
        </div>
      </header>
    </>
  )
}
